import * as React from 'react';
import { ComponentType, FunctionComponent, PropsWithChildren } from 'react';
import { useParams } from 'react-router';

import { useNavigateToRoute } from '../../../../../../shared/hooks/use-navigate-to-route/use-navigate-to-route.hook';
import useChangePrompt from '../../../../../shared/hooks/use-change-prompt/use-change-prompt.hook';
import { useGetReleasesFormSelectors } from '../../../ReleasesForm/hooks/use-get-releases-form-selectors/use-get-releases-form-selectors.hook';
import { useOnSubmitReleasesForm } from '../../../ReleasesForm/hooks/use-on-submit-releases-form/use-on-submit-releases-form.hook';
import { ReleasesFormProps } from '../../../ReleasesForm/models/releases-form-props.model';
import { ReleasesFormikModel } from '../../../ReleasesForm/models/releases-formik.model';
import { OuterExceptedAppReleasesFormProps } from '../../models/outer-excepted-app-releases-form.props';

type InjectedKeys =
  | 'exceptedAppReleasesFormStateFields'
  | 'apksIds'
  | 'appsFiles'
  | 'cancelAction'
  | 'parsedAppId';

type InjectedProps = Pick<OuterExceptedAppReleasesFormProps, InjectedKeys> & {
  onSubmitReleaseForm: (
    values: Partial<ReleasesFormikModel>
  ) => void | Promise<void>;
};

type PublicProps = Omit<
  OuterExceptedAppReleasesFormProps,
  keyof InjectedProps
> &
  ReleasesFormProps;

type PrivateProps = PublicProps & InjectedProps;

const withExceptedAppsReleasesForm = (
  Component: ComponentType<PrivateProps>
): FunctionComponent<PublicProps> => {
  return (props: PropsWithChildren<PublicProps>) => {
    const { data, submitThunk, changeFormState } = props;

    const { appsFiles, apksIds } = useGetReleasesFormSelectors();
    const navigateFn = useNavigateToRoute('../');

    const cancelAction = useChangePrompt(
      true,
      navigateFn,
      <span>Warning</span>,
      <span>Filled information will be lost. Leave anyway?</span>
    );

    const { id: appId } = useParams();
    const parsedAppId = parseInt(appId as string, 10);

    const onSubmitReleaseForm = useOnSubmitReleasesForm(
      parsedAppId,
      submitThunk,
      changeFormState,
      data
    );

    return (
      <Component
        {...props}
        apksIds={apksIds}
        appsFiles={appsFiles}
        cancelAction={cancelAction}
        parsedAppId={parsedAppId}
        onSubmitReleaseForm={onSubmitReleaseForm}
      />
    );
  };
};

export default withExceptedAppsReleasesForm;
